import { EnvConfig, MediaData, useAuthentication } from '@gripp/shared-logic';
import { useVideoPlayer, VideoView } from 'expo-video';
import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { ProcessingPlaceholder } from './processingPlaceholder';
import { MediaComponent, MediaComponentProps } from './types';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

/** In message history: cap width so video fits the bubble */
const MAX_VIDEO_WIDTH = 280;
const MIN_VIDEO_WIDTH = 160;
/** When no displayWidth (e.g. footer preview): compact size */
const PREVIEW_VIDEO_WIDTH = 200;
const ASPECT_RATIO = 9 / 16;
/** Space for native controls so they are not cut off */
const CONTROLS_BAR_HEIGHT = 64;

const isDirectPlaybackUrl = (url: string) =>
  url.startsWith('blob:') || url.startsWith('file:');

/** API returns { downloadUrl }; we need the actual URL for playback */
const isApiVideoUrl = (url: string) =>
  url.includes('/video/') &&
  (url.startsWith(EnvConfig.apiUrl) || url.startsWith('/'));

/** Resolve to full API URL (handles relative /video/:id when apiUrl is set) */
const getVideoApiUrl = (url: string): string => {
  if (!url || url.startsWith('http')) return url;
  const base = EnvConfig.apiUrl?.replace(/\/$/, '') || '';
  return base ? `${base}${url.startsWith('/') ? url : `/${url}`}` : url;
};

export const VideoComponent: MediaComponent<MediaData> = (
  props: MediaComponentProps<MediaData>
) => {
  const authentication = useAuthentication();
  const accessToken = authentication?.accessToken;
  const url = props.mediaItem.url ?? '';
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(() =>
    url && isDirectPlaybackUrl(url) ? url : null
  );
  const [resolveError, setResolveError] = useState(false);

  useEffect(() => {
    if (!url) {
      setPlaybackUrl(null);
      setResolveError(false);
      return;
    }
    setResolveError(false);
    if (isDirectPlaybackUrl(url)) {
      setPlaybackUrl(url);
      return;
    }
    if (!isApiVideoUrl(url)) {
      setPlaybackUrl(url);
      return;
    }
    const finalUrl = getVideoApiUrl(url);
    if (!finalUrl.startsWith('http')) {
      setResolveError(true);
      return;
    }
    if (authentication?.isLoading) {
      return;
    }
    let cancelled = false;
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    fetch(finalUrl, { credentials: 'omit', headers })
      .then((res) => {
        if (!res.ok)
          return Promise.reject(new Error(`${res.status} ${res.statusText}`));
        return res.json();
      })
      .then((data: { downloadUrl?: string }) => {
        if (!cancelled && data.downloadUrl) {
          setPlaybackUrl(data.downloadUrl);
        } else if (!cancelled) {
          setResolveError(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setResolveError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, accessToken, authentication?.isLoading]);

  const hasDisplayWidth =
    props.displayWidth !== undefined && props.displayWidth !== null;
  const isThumbnail =
    hasDisplayWidth && (props.displayWidth as number) < MIN_VIDEO_WIDTH;
  const maxWidth = isThumbnail
    ? (props.displayWidth as number)
    : hasDisplayWidth
    ? Math.min(
        Math.max(props.displayWidth as number, MIN_VIDEO_WIDTH),
        MAX_VIDEO_WIDTH
      )
    : PREVIEW_VIDEO_WIDTH;
  const videoHeight = useMemo(
    () => (isThumbnail ? maxWidth : Math.round(maxWidth * ASPECT_RATIO)),
    [maxWidth, isThumbnail]
  );
  const totalHeight = isThumbnail
    ? maxWidth
    : videoHeight + CONTROLS_BAR_HEIGHT;

  const videoSource = useMemo(
    () => (playbackUrl ? { uri: playbackUrl } : null),
    [playbackUrl]
  );

  const player = useVideoPlayer(videoSource);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setPlayerReady(false);
    setIsPlaying(false);
  }, [videoSource]);

  useEffect(() => {
    if (!player) return;
    if (player.status === 'readyToPlay') {
      setPlayerReady(true);
      return;
    }
    const subscription = player.addListener(
      'statusChange',
      ({ status, error }) => {
        if (status === 'readyToPlay' || status === 'error') {
          setPlayerReady(true);
        }
      }
    );
    return () => subscription.remove();
  }, [player]);

  useEffect(() => {
    if (!player) return;
    const subscription = player.addListener(
      'playingChange',
      ({ isPlaying: playing }) => {
        setIsPlaying(playing);
      }
    );
    return () => subscription.remove();
  }, [player]);

  if (!url) {
    return null;
  }

  if (resolveError) {
    return (
      <View
        style={[
          styles.wrapper,
          styles.errorWrapper,
          { maxWidth, minHeight: totalHeight },
        ]}
      >
        <Text style={styles.errorText}>Video unavailable</Text>
      </View>
    );
  }

  if (!playbackUrl) {
    return (
      <View
        style={[
          styles.wrapper,
          styles.loadingWrapper,
          { maxWidth, height: totalHeight },
        ]}
      >
        <ActivityIndicator color={Colors.primary} size="large" />
        <Text style={styles.loadingText}>Loading video…</Text>
      </View>
    );
  }

  return (
    <ProcessingPlaceholder
      processing={props.mediaItem.showProcessing ?? false}
      width={maxWidth}
      height={totalHeight}
      displayMode={props.displayMode ?? 'outlined'}
      url={props.mediaItem.url}
      name={props.mediaItem.name ?? ''}
      extension={props.mediaItem.extension}
    >
      <View style={[styles.wrapper, { maxWidth }]}>
        <VideoView
          player={player}
          style={[styles.video, { width: maxWidth, height: totalHeight }]}
          contentFit="contain"
          nativeControls
          allowsFullscreen
          allowsPictureInPicture={Platform.OS !== 'web'}
        />
        {!playerReady && Platform.OS !== 'web' && (
          <View style={[styles.videoLoadingOverlay, { height: totalHeight }]}>
            <ActivityIndicator color={Colors.primary} size="large" />
          </View>
        )}
        {playerReady && !isPlaying && (
          <Pressable
            style={[styles.playOverlay, { height: totalHeight }]}
            onPress={() => player.play()}
          >
            <View style={styles.playButton}>
              <View style={styles.playTriangle} />
            </View>
          </Pressable>
        )}
      </View>
    </ProcessingPlaceholder>
  );
};

const styles = themedStyleSheet((role) => ({
  wrapper: {
    width: '100%',
    alignSelf: 'flex-start',
  },
  loadingWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: role.surfaceRaised,
    borderRadius: 8,
  },
  loadingText: {
    marginTop: 8,
    fontSize: 12,
    color: role.textSecondary,
  },
  errorWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: role.surfaceRaised,
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    fontSize: 12,
    color: role.textSecondary,
  },
  video: {
    borderRadius: 8,
  },
  videoLoadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: role.surfaceRaised,
    borderRadius: 8,
  },
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  playButton: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  playTriangle: {
    width: 0,
    height: 0,
    borderTopWidth: 10,
    borderBottomWidth: 10,
    borderLeftWidth: 18,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: Colors.white,
    marginLeft: 4,
  },
}));
